import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Bubble } from '@tencent/tea-component';

import { AppState, useAppDispatch, useAppSelector } from '@store/index';
import { changeAudioListVisible, changeMicLoading, getMicId } from '@store/media/media.slice';
import { getAudioDevices, muteAudio, switchDefaultMic, switchMicPhone, unMuteAudio } from '@store/media/media.thunk';
import { getSelfInfo } from '@store/user/user.slice';

import { getAudioInputDevices, isUserAudioOn, queryPermissionStatus } from '@utils/helper/media';

import MediaDeviceList from '@component/media-device-list/MediaDeviceList';
import { MediaDeviceListItem } from '@component/media-device-list';
import { openToast } from '@component/toast';
import { RtcMediaPermissionStatus, RtcMediaPermissionType } from '@constant/media';
import { AudioCtrlProps } from './AudioCtrl.type';
import MicState from '../mic-state';

import './AudioCtrl.scss';
import { useOpenDialog } from '@component/dialog';
import { BOOL, HandsUpType, MediaType, MediaSettingType, MediaSettingOfAudio } from '@tencent/wemeet-websdk-next';
import weMeetClient from '@utils/wemeetClient';
import { getMeetingSettings } from '@store/meeting/meeting.slice';

const AudioCtrl: React.FC<AudioCtrlProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const openDialog = useOpenDialog();
  const meetingSetting = useAppSelector(getMeetingSettings);
  // @ts-ignore
  const audioSetting: MediaSettingOfAudio =
    meetingSetting.media_settings_list?.media_settings.find((el) => el.type === MediaSettingType.UP_AUDIO) || {};

  const micId = useAppSelector(getMicId);
  const { audioDeviceListVisible, speakerList, micList, micLoading, allowAudio } = useSelector((state: AppState) => ({
    audioLevel: state.media.audioLevel,
    micList: state.media.micList,
    speakerList: state.media.speakerList,
    audioDeviceListVisible: state.media.audioDeviceListVisible,
    micLoading: state.media.micLoading,
    allowAudio: state.media.allowAudio,
  }));

  const selfInfo = useAppSelector(getSelfInfo);
  const micStatus = isUserAudioOn(selfInfo);
  const handsup = selfInfo.handsup === BOOL.true;

  useEffect(() => {
    dispatch(getAudioDevices());
  }, []);
  /** state */

  const onArrowClick = () => {
    if (allowAudio || micList.length > 0) {
      // 如果有权限，
      dispatch(changeAudioListVisible(!audioDeviceListVisible));
    } else {
      // 如果不允许或者没数据重新请求
      dispatch(getAudioDevices()).then(() => {
        dispatch(changeAudioListVisible(!audioDeviceListVisible));
      });
    }
  };

  const changeAudioStatus = () => {
    dispatch(changeMicLoading(true));
    if (!micStatus) {
      // 静音状态开启音频
      dispatch(unMuteAudio());
    } else {
      // 有声音的情况关闭音频
      dispatch(muteAudio());
    }
  };

  const onAudioClick = async () => {
    if (micLoading) {
      return;
    }

    /** 全体静音且不允许自我解除静音时，打开弹窗询问是否举手/手放下 */
    const muteAll = audioSetting.mute_all === BOOL.true;
    const notAllowUnmute = audioSetting.allow_unmute_by_self === BOOL.false;

    if (notAllowUnmute && !micStatus && !muteAll) {
      openDialog({
        dialogId: 'unmuteNotAllowed',
        title: t('提醒'),
        content: t('主持人不允许开麦'),
        okText: handsup ? t('手放下') : t('举手申请'),
        cancelText: handsup ? t('保持举手') : t('取消'),
        onOk: handleModalClickConfirm,
      });
      return;
    }

    if (!micStatus && muteAll && notAllowUnmute) {
      openDialog({
        dialogId: 'muteAll',
        title: t('全体静音'),
        content: handsup ? t('您已经举手申请发言，是否取消举手申请？') : t('主持人已将全体静音，您可以举手申请发言。'),
        okText: handsup ? t('手放下') : t('举手申请'),
        cancelText: handsup ? t('保持举手') : t('取消'),
        onOk: handleModalClickConfirm,
      });
      return;
    }

    const deviceMicStatus = await queryPermissionStatus(RtcMediaPermissionType.Microphone);
    const devicePermissionGranted = deviceMicStatus !== RtcMediaPermissionStatus.Denied;
    const audioInputDevices = await getAudioInputDevices();

    if (audioInputDevices > 0) {
      if (devicePermissionGranted) {
        console.log('audioInputDevices:', audioInputDevices);
        changeAudioStatus();
      } else if (deviceMicStatus === RtcMediaPermissionStatus.Denied) {
        openDialog({
          dialogId: 'micPhoneError',
          title: t('麦克风未响应'),
          content: t('您的浏览器阻止了 VooV Meeting 访问麦克风\n请在浏览器设置中，将其设置为允许访问'),
          okText: t('我知道了'),
        });
      }
    } else {
      openToast(t('无录音设备'));
    }
  };

  const hide = (e: MouseEvent | undefined) => {
    if (!e) {
      dispatch(changeAudioListVisible(false));
      return;
    }

    const arrow = document.getElementsByClassName('audio-ctrl__arrow')?.[0];

    if (arrow?.contains(e.target as Node)) {
      return;
    }
    dispatch(changeAudioListVisible(false));
  };

  const onDeviceChange = (device: MediaDeviceInfo | boolean, type?: MediaDeviceListItem['className']) => {
    // 这里不需要处理扬声器，因为扬声器只有默认的
    if (type !== 'micphone') {
      return;
    }

    if (device) {
      // 选非默认的情况
      dispatch(switchMicPhone(device as MediaDeviceInfo));
      return;
    }

    // 选默认的情况
    if (!micId) {
      // 当前是默认，直接返回
      openToast(t('切换成功'));
      return;
    }

    dispatch(switchDefaultMic());
  };

  /** ******** 以下为举手弹窗逻辑 ************* */

  /** 点击确认按钮 */
  const handleModalClickConfirm = () => {
    const handsupType = handsup ? HandsUpType.cancelHandsUp : HandsUpType.handsUp;
    weMeetClient.mediaCtrl
      .handsUp(handsupType, [MediaType.AUDIO])
      .then(() => {
        !handsup && openToast(t('举手成功，等待主持人操作'));
      })
      .catch(() => {
        !handsup && openToast(t('举手失败'));
      });
  };

  return (
    <div className="audio-ctrl">
      <div className="audio-ctrl__btn" onClick={onAudioClick}>
        <MicState micOn={isUserAudioOn(selfInfo)} mode="large" stream={selfInfo?.rtcStream?.stream} />
        <p>{isUserAudioOn(selfInfo) ? t('静音') : t('解除静音')}</p>
      </div>

      <Bubble
        trigger="click"
        className="device-poppver"
        visible={audioDeviceListVisible}
        onVisibleChange={() => {}} // 组件要求必须要
        content={
          <MediaDeviceList
            onHide={hide}
            onChange={onDeviceChange}
            dataset={[
              {
                data: speakerList,
                title: t('选择扬声器'),
                className: 'speaker',
                showDefault: false,
                alwaysDefault: true,
              },
              {
                data: micList,
                title: t('选择麦克风'),
                className: 'micphone',
                showDefault: true,
                alwaysDefault: false,
                selectedId: micId,
              },
            ]}
          />
        }
      >
        <div className="audio-ctrl__arrow" onClick={onArrowClick} />
      </Bubble>
    </div>
  );
};

export default React.memo(AudioCtrl);
